import { useState } from 'react'
import { formatAllergens } from '@/lib/allergens'
import { availableAlternatives, changeAlternative, choiceCount, isSingleChoice, validAlternativeChoices } from '@/lib/alternatives'
import type { AlternativeGroup, CartItem, Product, SelectedAlternative } from '@/lib/types'
import { fmt } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

export default function ProductModal() {
  const selectedProduct = useKioskStore((s) => s.selectedProduct)
  const setSelectedProduct = useKioskStore((s) => s.setSelectedProduct)
  const addToCart = useKioskStore((s) => s.addToCart)
  const editingItem = useKioskStore((s) => s.editingCartItem)
  const saveCartItem = useKioskStore((s) => s.saveCartItem)

  if (!selectedProduct) return null

  return (
    <ProductModalInner
      key={`${selectedProduct.properties?.id}:${editingItem ? 'edit' : 'add'}`}
      product={selectedProduct}
      editingItem={editingItem}
      onAdd={(p, alts, msg) => {
        if (editingItem) {
          saveCartItem(alts, msg)
          return
        }
        addToCart(p, alts, msg)
        setSelectedProduct(null)
        const store = useKioskStore.getState()
        const postAdd = store.afterProductAddedAction ?? store.backAction
        postAdd?.()
      }}
      onClose={() => setSelectedProduct(null)}
    />
  )
}

function ProductModalInner({
  product,
  editingItem,
  onAdd,
  onClose,
}: {
  product: Product
  editingItem: CartItem | null
  onAdd: (p: Product, alts: SelectedAlternative[], message?: string) => void
  onClose: () => void
}) {
  const locale = useKioskStore((s) => s.locale)
  const groups = product.properties?.alternativeGroups || []
  const props = product.properties

  const [itemMessage, setItemMessage] = useState(editingItem?.message ?? '')
  const [showInfo, setShowInfo] = useState(false)

  // Alternative selections
  const [errorGroups, setErrorGroups] = useState<Set<string>>(new Set())
  const [selections, setSelections] = useState<Record<string, Record<string, number>>>(() => {
    const init: Record<string, Record<string, number>> = {}
    groups.forEach((g) => {
      init[g.id] = {}
      availableAlternatives(g).forEach((ai) => {
        if (editingItem) {
          const existing = editingItem.alternatives.find((alternative) => alternative.alternativeGroupId === g.id && alternative.alternativeItemName === ai.name)
          if (existing) init[g.id][ai.name] = existing.count
        } else if (ai.isDefault) init[g.id][ai.name] = 1
      })
    })
    return init
  })

  const handleSelect = (groupId: string, itemName: string, group: AlternativeGroup) => {
    setErrorGroups((prev) => {
      const next = new Set(prev)
      next.delete(groupId)
      return next
    })
    setSelections((prev) => {
      const current = prev[groupId] || {}
      return { ...prev, [groupId]: changeAlternative(group, current, itemName, current[itemName] ? 0 : 1) }
    })
  }

  const handleAdjust = (event: React.MouseEvent, groupId: string, itemName: string, delta: number, group: AlternativeGroup) => {
    event.stopPropagation()
    setSelections((prev) => {
      const current = prev[groupId] || {}
      return { ...prev, [groupId]: changeAlternative(group, current, itemName, (current[itemName] || 0) + delta) }
    })
  }

  const addonTotal = groups.reduce((total, g) => {
    const sel = selections[g.id] || {}
    return (
      total +
      Object.entries(sel).reduce((sum, [name, count]) => {
        const item = g.alternativeItems.find((ai) => ai.name === name)
        return sum + (item?.addonProduct?.properties?.price || 0) * count
      }, 0)
    )
  }, 0)

  const handleAdd = () => {
    const missing = groups.filter((group) => !validAlternativeChoices(group, selections[group.id] || {}))
    if (missing.length > 0) {
      setErrorGroups(new Set(missing.map((g) => g.id)))
      return
    }
    const alts: SelectedAlternative[] = []
    groups.forEach((g) => {
      Object.entries(selections[g.id] || {}).forEach(([name, count]) => {
        if (count <= 0) return
        const item = g.alternativeItems.find((ai) => ai.name === name)
        const alt: SelectedAlternative = {
          alternativeGroupId: g.id,
          alternativeItemName: name,
          count,
        }
        if (item?.addonProduct?.properties) {
          alt.addonProduct = {
            productId: item.addonProduct.properties.id,
            productStateId: item.addonProduct.properties.stateId,
            productPrice: item.addonProduct.properties.price,
          }
        }
        alts.push(alt)
      })
    })
    onAdd(product, alts, itemMessage)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center kiosk-fade-in">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-kiosk-surface w-[92%] max-w-5xl max-h-[94dvh] flex flex-col rounded-3xl z-10 kiosk-slide-up overflow-hidden">
        {/* Scrollable content */}
        <div className="overflow-y-auto kiosk-scroll flex-1 min-h-0">
          {/* Image — full width, no crop */}
          {props?.imageUrl && (
            <div className="relative w-full overflow-hidden bg-kiosk-surface-2">
              <img src={props?.imageUrl} alt={product.name} className="w-full max-h-[38dvh] object-contain" />
            </div>
          )}

          <div className="px-6 space-y-5">
            {/* Title + price */}
            <div className="pt-4">
              {editingItem && (
                <p className="text-kiosk-accent text-base mb-2">
                  {locale === 'sv' ? `Ändrar ${editingItem.count} st — gäller hela raden` : `Editing ${editingItem.count} items — applies to the whole line`}
                </p>
              )}
              <div className="flex justify-between items-start gap-3">
                <h3 className="font-bold text-2xl text-kiosk-text">{product.name}</h3>
                {!!props?.price && (
                  <div className="shrink-0 flex flex-col items-end leading-tight">
                    {props.preCampaignPrice && props.preCampaignPrice > props.price && <span className="text-kiosk-muted line-through text-sm">{fmt(props.preCampaignPrice)}</span>}
                    <span className="text-kiosk-accent font-bold text-xl">{fmt(props.price)}</span>
                  </div>
                )}
              </div>
              {props?.description && <p className="text-kiosk-muted text-base mt-2">{props.description}</p>}
            </div>

            {/* Info-knapp ("i" i ring) — oppnar allergener + ingredienser +
                naringsvarden pa begaran. Osynlig om produkten inte har nagot. */}
            {(() => {
              const allergenLabels = formatAllergens(product.allergens, locale)
              const ingredientsText = locale === 'en' ? product.ingredientsTextEn || product.ingredientsTextSv || '' : product.ingredientsTextSv || ''
              // Samla naringsvarden som faktiskt finns (null = ej satt)
              const nutritionRows: { label: string; value: string }[] = []
              const sv = locale === 'sv'
              // mg till g-konvertering for lasbarhet (alla mg-varden)
              const mg = (n: number | null, label: string) => {
                if (n == null) return
                const g = n / 1000
                nutritionRows.push({ label, value: `${g.toFixed(g < 1 ? 2 : 1)} g` })
              }
              if (product.energyKcal != null) {
                nutritionRows.push({
                  label: sv ? 'Energi' : 'Energy',
                  value: `${product.energyKcal} kcal`,
                })
              }
              mg(product.fatMg, sv ? 'Fett' : 'Fat')
              mg(product.saturatedFatMg, sv ? 'varav mättat fett' : 'of which saturated')
              mg(product.carbohydratesMg, sv ? 'Kolhydrater' : 'Carbohydrates')
              mg(product.sugarsMg, sv ? 'varav socker' : 'of which sugars')
              mg(product.proteinMg, sv ? 'Protein' : 'Protein')
              mg(product.saltMg, sv ? 'Salt' : 'Salt')
              mg(product.fiberMg, sv ? 'Fiber' : 'Fibre')

              const hasInfo = allergenLabels.length > 0 || !!ingredientsText.trim() || nutritionRows.length > 0
              if (!hasInfo) return null
              return (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowInfo((v) => !v)}
                    className="kiosk-btn inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-kiosk-surface-2 text-kiosk-muted hover:text-kiosk-text text-sm"
                    aria-expanded={showInfo}
                  >
                    <span className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-current text-[11px] font-bold">i</span>
                    {showInfo ? (sv ? 'Dölj info' : 'Hide info') : sv ? 'Mer info' : 'More info'}
                  </button>
                  {showInfo && (
                    <div className="mt-3 space-y-3 p-4 bg-kiosk-bg border border-kiosk-surface-2 rounded-2xl">
                      {allergenLabels.length > 0 && (
                        <div>
                          <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-1">{sv ? 'Allergener' : 'Allergens'}</p>
                          <p className="text-kiosk-text text-sm">{allergenLabels.join(', ')}</p>
                        </div>
                      )}
                      {ingredientsText && (
                        <div>
                          <p className="text-kiosk-muted text-xs font-semibold uppercase tracking-wider mb-1">{sv ? 'Ingredienser' : 'Ingredients'}</p>
                          <p className="text-kiosk-text text-sm">{ingredientsText}</p>
                        </div>
                      )}
                      {nutritionRows.length > 0 && (
                        <div>
                          <p className="text-kiosk-muted text-xs font-semibold uppercase tracking-wider mb-2">
                            {sv ? 'Näringsvärden' : 'Nutrition'}
                            <span className="text-kiosk-muted/60 font-normal ml-2">({sv ? 'per portion' : 'per serving'})</span>
                          </p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            {nutritionRows.map((row) => (
                              <div key={row.label} className="flex justify-between">
                                <span className="text-kiosk-muted">{row.label}</span>
                                <span className="text-kiosk-text">{row.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Native LocoPOS-alternativgrupper. */}
            {groups.map((group) => {
              const sel = selections[group.id] || {}
              const totalSel = choiceCount(group, sel)
              const min = group.minChoices ?? 0
              const max = group.maxChoices ?? Infinity
              const isSingle = isSingleChoice(group)
              const allowMultiCount = group.isCountable === true
              const isRequired = min > 0
              const fulfilled = !isRequired || totalSel >= min
              const hasError = errorGroups.has(group.id)
              const displayTitle = group.name

              const sv = locale === 'sv'
              let label = sv ? 'Valfritt' : 'Optional'
              if (isRequired) {
                if (isSingle) label = sv ? 'Välj 1' : 'Choose 1'
                else if (min === max) label = `${sv ? 'Välj' : 'Choose'} ${min}`
                else label = `${sv ? 'Välj' : 'Choose'} ${min}${Number.isFinite(max) ? `-${max}` : '+'}`
              } else if (Number.isFinite(max)) {
                label = `Max ${max}`
              }

              return (
                <div
                  key={group.id}
                  className={`space-y-3 p-4 rounded-2xl transition-all ${
                    hasError ? 'ring-2 ring-red-500/70 bg-red-500/10' : isRequired && !fulfilled ? 'ring-1 ring-kiosk-accent/30 bg-kiosk-accent/5' : ''
                  }`}
                >
                  {/* Centrerad, större fråga från det native gruppnamnet. */}
                  <div className="flex flex-col items-center gap-1.5 pt-1">
                    <h4 className="text-kiosk-text font-bold text-2xl text-center">{displayTitle}</h4>
                    <span
                      className={`text-xs px-3 py-1 rounded-full font-semibold ${
                        hasError
                          ? 'bg-red-500/20 text-red-400'
                          : isRequired && !fulfilled
                            ? 'bg-kiosk-accent text-kiosk-bg'
                            : fulfilled && isRequired
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-kiosk-surface-2 text-kiosk-muted'
                      }`}
                    >
                      {hasError ? (sv ? 'Kontrollera dina val' : 'Check your choices') : fulfilled && isRequired ? `✓ ${label}` : label}
                    </span>
                    {group.maxChoices != null && <p className="text-kiosk-muted text-sm">{sv ? `${totalSel} av max ${max} valda` : `${totalSel} of max ${max} selected`}</p>}
                  </div>

                  <div className="space-y-2">
                    {availableAlternatives(group).map((item) => {
                      const isSelected = (sel[item.name] || 0) > 0
                      const count = sel[item.name] || 0
                      const addonPrice = item.addonProduct?.properties?.price || 0
                      const addonImage = item.addonProduct?.properties?.imageUrl || null
                      const itemName = item.name

                      // Default list-layout (rad med checkbox + vänsterbild)
                      return (
                        <div
                          key={item.name}
                          onClick={() => handleSelect(group.id, item.name, group)}
                          className={`flex items-center justify-between p-4 rounded-xl transition-all ${isSelected ? 'bg-kiosk-surface-2 ring-1 ring-kiosk-accent/40' : 'bg-kiosk-bg'}`}
                        >
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            {/* Checkbox/radio */}
                            <div
                              className={`w-7 h-7 shrink-0 border-2 flex items-center justify-center ${
                                isSingle ? 'rounded-full' : 'rounded-lg'
                              } ${isSelected ? 'border-kiosk-accent bg-kiosk-accent' : 'border-kiosk-muted/40'}`}
                            >
                              {isSelected && (
                                <svg className="w-4 h-4 text-kiosk-bg" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            {/* Image (if available) */}
                            {addonImage && <img src={addonImage} alt={itemName} className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                            <span className="text-kiosk-text text-base">{itemName}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {addonPrice > 0 && <span className="text-kiosk-accent text-sm">+{fmt(addonPrice)}</span>}
                            {allowMultiCount && isSelected && (
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={(e) => handleAdjust(e, group.id, item.name, -1, group)}
                                  aria-label={sv ? `Minska ${itemName}` : `Decrease ${itemName}`}
                                  className="kiosk-btn w-10 h-10 rounded-full bg-kiosk-bg text-kiosk-text flex items-center justify-center text-xl font-bold"
                                >
                                  &minus;
                                </button>
                                <span className="text-kiosk-text text-lg w-6 text-center">{count}</span>
                                <button
                                  onClick={(e) => handleAdjust(e, group.id, item.name, 1, group)}
                                  disabled={totalSel + (item.countableUsage ?? 1) > max}
                                  aria-label={sv ? `Öka ${itemName}` : `Increase ${itemName}`}
                                  className="kiosk-btn w-10 h-10 rounded-full bg-kiosk-accent text-kiosk-bg flex items-center justify-center text-xl font-bold disabled:opacity-30"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
            {/* Special request — "görs om möjligt" / "if possible" */}
            <div>
              <p className="text-kiosk-text font-semibold text-sm mb-1.5">
                {locale === 'en' ? 'Special request' : 'Meddelande'}
                <span className="text-kiosk-muted font-normal ml-2">({locale === 'en' ? 'done if possible' : 'görs om möjligt'})</span>
              </p>
              <textarea
                value={itemMessage}
                onChange={(e) => setItemMessage(e.target.value.slice(0, 200))}
                placeholder={locale === 'en' ? 'E.g. no peanuts, mild spice, etc.' : 'T.ex. utan jordnötter, milt, etc.'}
                rows={2}
                className="w-full bg-kiosk-bg border border-kiosk-surface-2 rounded-xl px-3 py-2 text-kiosk-text placeholder:text-kiosk-muted text-sm focus:outline-none focus:border-kiosk-accent/60 resize-none"
              />
            </div>

            {/* Add + Cancel buttons — after all alternative groups */}
            <div className="space-y-2 pt-2 pb-4">
              <button onClick={handleAdd} className="kiosk-btn w-full py-4 bg-kiosk-accent text-kiosk-bg font-bold rounded-xl text-lg">
                {editingItem ? (locale === 'en' ? 'Save changes' : 'Spara ändringar') : locale === 'en' ? 'Add' : 'Lägg till'} — {fmt(((props?.price ?? 0) + addonTotal) * (editingItem?.count ?? 1))}
              </button>
              <button onClick={onClose} className="kiosk-btn w-full py-2 text-kiosk-muted text-sm">
                {locale === 'en' ? 'Cancel' : 'Avbryt'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
